import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Paper,
  Grid,
  Button,
} from "@mui/material";
import { camelCaseToSentenceCase } from "../../../helpers/camelCaseToSentenceCase";
import { formatLoanValues } from "../../../helpers/formatingFunctions";
import UpdateLoanPopup from "../UpdateLoanPopup/UpdateLoanPopup";
import LenderInfoModal from "../LenderInfoModal/LenderInfoModal";
import LoanUnitsDrawer from "../LoanUnitsDrawer/LoanUnitsDrawer";

interface LoanInformationProps {
  asset: any;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

const LoanInformation: React.FC<LoanInformationProps> = ({
  asset,
  isLoading,
  isError,
  error,
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(null);
  const [selectedLoanNumber, setSelectedLoanNumber] = useState<string | null>(
    null,
  );
  const [lenderModalOpen, setLenderModalOpen] = useState(false);
  const [selectedLenderId, setSelectedLenderId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, bgcolor: "error.light", color: "error.contrastText" }}
      >
        <Typography>Error: {error?.message}</Typography>
      </Paper>
    );
  }

  if (!asset) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 3 },
          height: "100%",
          bgcolor: "background.paper",
        }}
      >
        <Box
          className="asset-detail-field"
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            pb: 2,
            borderBottom: "2px solid",
            borderColor: "primary.main",
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: "primary.main",
            }}
          >
            Loan Information
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary" align="center">
          This asset is not financed
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 1.5, sm: 3 },
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      <Box
        className="asset-detail-field"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          pb: 2,
          borderBottom: "2px solid",
          borderColor: "primary.main",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: "primary.main",
          }}
        >
          Loan Information
        </Typography>
        <UpdateLoanPopup loan={asset} />
      </Box>

      <Grid container spacing={2}>
        {Object.entries(asset)
          .filter(
            ([key]) =>
              !["id", "loanId", "lenderId", "estimatedEndDate"].includes(key),
          )
          .map(([key, value]) => {
            if (key === "loanNumber") {
              return (
                <Grid item xs={12} key={key}>
                  <Box
                    className="asset-detail-field"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderRadius: 1,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {camelCaseToSentenceCase(key)}:
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => {
                        setSelectedLoanId(asset.id);
                        setSelectedLoanNumber(String(value));
                        setDrawerOpen(true);
                      }}
                      sx={{
                        textTransform: "none",
                        color: "primary.main",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {formatLoanValues(key, String(value))}
                    </Button>
                  </Box>
                </Grid>
              );
            }

            if (key === "lenderName") {
              return (
                <Grid item xs={12} key={key}>
                  <Box
                    className="asset-detail-field"
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderRadius: 1,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        color: "text.secondary",
                      }}
                    >
                      {camelCaseToSentenceCase(key)}:
                    </Typography>
                    <Button
                      variant="text"
                      onClick={() => {
                        setSelectedLenderId(asset.lenderId);
                        setLenderModalOpen(true);
                      }}
                      sx={{
                        textTransform: "none",
                        color: "primary.main",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {value as string}
                    </Button>
                  </Box>
                </Grid>
              );
            }

            if (key === "estimatedRemainingBalance") {
              return (
                <Grid item xs={12} key={key}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      p: 1.5,
                      borderRadius: 1,
                      "&:hover": {
                        bgcolor: "action.hover",
                      },
                    }}
                  >
                    <Box
                      className="asset-detail-field"
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                          color: "text.secondary",
                        }}
                      >
                        {camelCaseToSentenceCase(key)}:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: "text.primary",
                        }}
                      >
                        {formatLoanValues(key, String(value))}
                      </Typography>
                    </Box>

                    {asset.estimatedEndDate && (
                      <Box
                        className="asset-detail-field"
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "text.secondary",
                          }}
                        >
                          Estimated End Date:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            color: "text.primary",
                          }}
                        >
                          {formatLoanValues(
                            "estimatedEndDate",
                            String(asset.estimatedEndDate),
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              );
            }

            return (
              <Grid item xs={12} key={key}>
                <Box
                  className="asset-detail-field"
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    p: 1.5,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "text.secondary",
                    }}
                  >
                    {camelCaseToSentenceCase(key)}:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {formatLoanValues(key, String(value))}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
      </Grid>

      <LoanUnitsDrawer
        loanId={selectedLoanId}
        loanNumber={selectedLoanNumber}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />

      <LenderInfoModal
        id={selectedLenderId || ""}
        open={lenderModalOpen}
        onClose={() => setLenderModalOpen(false)}
      />
    </Paper>
  );
};

export default LoanInformation;
